import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import { fetchAndActivate, getAll } from '@firebase/remote-config'
import useLocalStorage from '@/hooks/useLocalStorage'
import { remoteConfig } from './firebase_config'

type Flags = Record<string, boolean>
type FeatureFlagContextValue = {
  featureFlags: Flags
  remoteFlags: Flags
  storedFlags: Flags
  updateFlag: (key: string, value: boolean) => void
  clearStoredFlags: () => void
}

const defaults: FeatureFlagContextValue = {
  updateFlag: () => {},
  clearStoredFlags: () => {},
  remoteFlags: {},
  storedFlags: {},
  featureFlags: {
    is_internal_user: false,
    customer_page_visible: false,
    custom_python_rules: false,
    show_action_python_script: false,
    show_billing_page: false,
    create_field_by_info_type: false,
    use_new_run_prediction_model: false,
    show_rules_tab_in_workflow_states: false,
    auto_sort_documents_card: false,
  },
}

export const FeatureFlagContext =
  createContext<FeatureFlagContextValue>(defaults)

export const useFeatureFlagContext = () => {
  const { featureFlags } = useContext(FeatureFlagContext)
  return featureFlags
}

type FeatureFlagProviderProps = {
  children: ReactNode
}

export default function FeatureFlagProvider({
  children,
}: FeatureFlagProviderProps) {
  const [storedFlags, setStoredFlags] = useLocalStorage<Flags>(
    'feature-flags',
    {},
  )
  const [remoteFlags, setRemoteFlags] = useState<Flags>(defaults.featureFlags)

  const fetchAndSetRemoteFlags = useCallback(
    async ({
      forceFetch = false,
      defaultConfig,
    }: {
      forceFetch?: boolean
      defaultConfig?: Flags
    } = {}) => {
      const remoteConfigInstance = await remoteConfig()
      if (!remoteConfigInstance) {
        // eslint-disable-next-line no-console
        console.debug('Remote Config is not supported in this environment.')
        return
      }
      if (forceFetch) {
        // Set minimum fetch interval to 0 to force fetch
        remoteConfigInstance.settings.minimumFetchIntervalMillis = 0
      }

      if (defaultConfig) {
        remoteConfigInstance.defaultConfig = defaultConfig
      }
      return fetchAndActivate(remoteConfigInstance)
        .then((neededActivation) => {
          // eslint-disable-next-line no-console
          console.debug(
            `Remote Feature flags ${neededActivation ? 'updated' : 'fetched'}`,
          )
          return getAll(remoteConfigInstance)
        })
        .then((firebaseFlags) => {
          const newFlags: Flags = {}
          Object.entries(firebaseFlags).forEach(([key, value]) => {
            newFlags[key] = value.asBoolean()
          })
          setRemoteFlags(newFlags)
        })
    },
    [],
  )

  useEffect(() => {
    fetchAndSetRemoteFlags({
      defaultConfig: defaults.featureFlags,
    })
  }, [fetchAndSetRemoteFlags])

  function updateFlag(key: string, value: boolean) {
    setStoredFlags({
      ...storedFlags,
      [key]: value,
    })
  }

  async function clearStoredFlags() {
    setStoredFlags({})
    fetchAndSetRemoteFlags({ forceFetch: true })
  }

  const flags = useMemo(
    () => ({
      ...defaults.featureFlags,
      ...remoteFlags,
      ...storedFlags,
    }),
    [remoteFlags, storedFlags],
  )

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlags: flags,
        remoteFlags,
        storedFlags,
        updateFlag,
        clearStoredFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  )
}
